// Constants to avoid magic numbers
const DEFAULT_TTL_MINUTES = 15;
const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_CLEANUP_INTERVAL_MINUTES = 5;
const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const BYTES_PER_KB = 1024;
const KB_PER_MB = 1024;

export interface CacheItem<T = unknown>
{
    data: T;
    timestamp: number;
    expiresAt: number;
    size: number;
}

export interface CacheConfig
{
    defaultTTL: number; // Time to live in milliseconds
    maxSize: number; // Maximum cache size in bytes
    cleanupInterval: number; // Auto cleanup interval in milliseconds
    keyPrefix: string; // Prefix for cache keys
}

export interface CacheStats
{
    totalItems: number;
    totalSize: number;
    hitCount: number;
    missCount: number;
    expiredCount: number;
}

/**
 * SocialCache - A robust localStorage-based cache system with automatic expiration
 * Features:
 * - Automatic data expiration
 * - Storage size management
 * - Type-safe operations
 * - Error handling and fallbacks
 * - Cache statistics and cleanup utilities
 *
 * @example
 * ```typescript
 * // Create a cache instance with custom configuration
 * const cache = new SocialCache({
 *   defaultTTL: 1000 * 60 * 30, // 30 minutes
 *   maxSize: 1024 * 1024 * 10,  // 10MB
 *   keyPrefix: 'my_app_cache_'
 * });
 *
 * // Cache user data
 * interface User {
 *   id: number;
 *   name: string;
 *   email: string;
 * }
 *
 * const user: User = { id: 1, name: 'John Doe', email: 'john@example.com' };
 * cache.set('user_1', user, 1000 * 60 * 60); // Cache for 1 hour
 *
 * // Retrieve cached data with type safety
 * const cachedUser = cache.get<User>('user_1');
 * if (cachedUser) {
 *   console.log('Found user:', cachedUser.name);
 * }
 *
 * // Use memoization for expensive operations
 * const fetchUserData = cache.memoize(
 *   async (userId: number) => {
 *     const response = await fetch(`/api/users/${userId}`);
 *     return response.json();
 *   },
 *   (userId) => `user_data_${userId}`,
 *   1000 * 60 * 15 // Cache for 15 minutes
 * );
 *
 * // Check cache statistics
 * const stats = cache.getStats();
 * console.log(`Cache hit ratio: ${cache.getHitRatio() * 100}%`);
 * console.log(`Total items: ${stats.totalItems}`);
 * ```
 */
export class SocialCache
{
    private config: CacheConfig;

    private stats: CacheStats;

    private cleanupTimer: number | null = null;

    public constructor(config: Partial<CacheConfig> = {})
    {
        this.config = {
            defaultTTL: MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * DEFAULT_TTL_MINUTES,
            maxSize: BYTES_PER_KB * KB_PER_MB * DEFAULT_MAX_SIZE_MB,
            cleanupInterval: MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * DEFAULT_CLEANUP_INTERVAL_MINUTES,
            keyPrefix: "social_cache_",
            ...config
        };

        this.stats = {
            totalItems: 0,
            totalSize: 0,
            hitCount: 0,
            missCount: 0,
            expiredCount: 0
        };

        this.initializeCache();
    }

    /**
     * Initialize the cache system
     */
    private initializeCache(): void
    {
        this.updateStats();
        this.startAutoCleanup();

        // Clean up expired items on initialization
        this.cleanupExpired();
    }

    /**
     * Start automatic cleanup timer
     */
    private startAutoCleanup(): void
    {
        if (this.cleanupTimer)
        {
            clearInterval(this.cleanupTimer);
        }

        this.cleanupTimer = window.setInterval(() =>
        {
            this.cleanupExpired();
        }, this.config.cleanupInterval);
    }

    /**
     * Stop automatic cleanup timer
     */
    public stopAutoCleanup(): void
    {
        if (this.cleanupTimer)
        {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
    }

    /**
     * Generate a prefixed cache key
     */
    private getCacheKey(key: string): string
    {
        return `${this.config.keyPrefix}${key}`;
    }

    /**
     * Calculate the size of data in bytes
     */
    private calculateSize(data: unknown): number
    {
        try
        {
            return new Blob([JSON.stringify(data)]).size;
        }
        catch
        {
            // Fallback calculation
            return JSON.stringify(data).length * 2;
        }
    }

    /**
     * Check if localStorage is available and working
     */
    private isLocalStorageAvailable(): boolean
    {
        try
        {
            const testKey = "__cache_test__";

            localStorage.setItem(testKey, "test");
            localStorage.removeItem(testKey);

            return true;
        }
        catch
        {
            return false;
        }
    }

    /**
     * Set an item in the cache
     */
    public set(key: string, data: unknown, ttl?: number): boolean
    {
        if (!this.isLocalStorageAvailable())
        {
            // localStorage is not available
            return false;
        }

        try
        {
            const now = Date.now();
            const expirationTime = ttl ?? this.config.defaultTTL;
            const size = this.calculateSize(data);

            // Check if adding this item would exceed max size
            if (this.stats.totalSize + size > this.config.maxSize)
            {
                this.makeSpace(size);
            }

            const cacheItem: CacheItem = {
                data,
                timestamp: now,
                expiresAt: now + expirationTime,
                size
            };

            const cacheKey = this.getCacheKey(key);

            localStorage.setItem(cacheKey, JSON.stringify(cacheItem));

            this.updateStats();

            return true;
        }
        catch
        {
            // Failed to set cache item
            return false;
        }
    }

    /**
     * Get an item from the cache
     */
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    public get<T>(key: string): T | null
    {
        if (!this.isLocalStorageAvailable())
        {
            return null;
        }

        try
        {
            const cacheKey = this.getCacheKey(key);
            const stored = localStorage.getItem(cacheKey);

            if (!stored)
            {
                this.stats.missCount++;

                return null;
            }

            const cacheItem: CacheItem<T> = JSON.parse(stored) as CacheItem<T>;
            const now = Date.now();

            // Check if item has expired
            if (now > cacheItem.expiresAt)
            {
                this.delete(key);
                this.stats.expiredCount++;
                this.stats.missCount++;

                return null;
            }

            this.stats.hitCount++;

            return cacheItem.data;
        }
        catch
        {
            // Failed to get cache item
            this.stats.missCount++;

            return null;
        }
    }

    /**
     * Check if an item exists and is not expired
     */
    public has(key: string): boolean
    {
        if (!this.isLocalStorageAvailable())
        {
            return false;
        }

        try
        {
            const cacheKey = this.getCacheKey(key);
            const stored = localStorage.getItem(cacheKey);

            if (!stored)
            {
                return false;
            }

            const cacheItem: CacheItem = JSON.parse(stored) as CacheItem;
            const now = Date.now();

            if (now > cacheItem.expiresAt)
            {
                this.delete(key);

                return false;
            }

            return true;
        }
        catch
        {
            return false;
        }
    }

    /**
     * Delete an item from the cache
     */
    public delete(key: string): boolean
    {
        if (!this.isLocalStorageAvailable())
        {
            return false;
        }

        try
        {
            const cacheKey = this.getCacheKey(key);

            localStorage.removeItem(cacheKey);
            this.updateStats();

            return true;
        }
        catch
        {
            // Failed to delete cache item
            return false;
        }
    }

    /**
     * Clear all cache items
     */
    public clear(): void
    {
        if (!this.isLocalStorageAvailable())
        {
            return;
        }

        try
        {
            const keys = Object.keys(localStorage).filter(key => key.startsWith(this.config.keyPrefix));

            keys.forEach(key => void localStorage.removeItem(key));
            this.updateStats();
        }
        catch
        {
            // Failed to clear cache
        }
    }

    /**
     * Clean up expired items
     */
    public cleanupExpired(): number
    {
        if (!this.isLocalStorageAvailable())
        {
            return 0;
        }

        let cleanedCount = 0;

        const now = Date.now();

        try
        {
            const keys = Object.keys(localStorage).filter(key => key.startsWith(this.config.keyPrefix));

            for (const key of keys)
            {
                try
                {
                    const stored = localStorage.getItem(key);

                    if (!stored) continue;

                    const cacheItem: CacheItem = JSON.parse(stored) as CacheItem;

                    if (now > cacheItem.expiresAt)
                    {
                        localStorage.removeItem(key);
                        cleanedCount++;
                    }
                }
                catch
                {
                    // Remove corrupted items
                    localStorage.removeItem(key);
                    cleanedCount++;
                }
            }

            if (cleanedCount > 0)
            {
                this.updateStats();
            }
        }
        catch
        {
            // Failed to cleanup expired items
        }

        return cleanedCount;
    }

    /**
     * Make space in the cache by removing oldest items
     */
    private makeSpace(requiredSize: number): void
    {
        if (!this.isLocalStorageAvailable())
        {
            return;
        }

        try
        {
            const cacheItems: { key: string; item: CacheItem }[] = [];

            // Collect all cache items with timestamps
            const keys = Object.keys(localStorage).filter(key => key.startsWith(this.config.keyPrefix));

            for (const key of keys)
            {
                try
                {
                    const stored = localStorage.getItem(key);

                    if (stored)
                    {
                        const item: CacheItem = JSON.parse(stored) as CacheItem;

                        cacheItems.push({ key, item });
                    }
                }
                catch
                {
                    // Remove corrupted items
                    localStorage.removeItem(key);
                }
            }

            // Sort by timestamp (oldest first)
            cacheItems.sort((a, b) => a.item.timestamp - b.item.timestamp);

            // Remove items until we have enough space
            let freedSpace = 0;

            for (const { key } of cacheItems)
            {
                if (freedSpace >= requiredSize) break;

                const stored = localStorage.getItem(key);

                if (stored)
                {
                    const item: CacheItem = JSON.parse(stored) as CacheItem;

                    freedSpace += item.size;
                    localStorage.removeItem(key);
                }
            }
        }
        catch
        {
            // Failed to make space in cache
        }
    }

    /**
     * Update cache statistics
     */
    private updateStats(): void
    {
        if (!this.isLocalStorageAvailable())
        {
            return;
        }

        try
        {
            let totalItems = 0;
            let totalSize = 0;

            const keys = Object.keys(localStorage).filter(key => key.startsWith(this.config.keyPrefix));

            for (const key of keys)
            {
                try
                {
                    const stored = localStorage.getItem(key);

                    if (stored)
                    {
                        const item: CacheItem = JSON.parse(stored) as CacheItem;

                        totalItems++;
                        totalSize += item.size;
                    }
                }
                catch
                {
                    // Skip corrupted items
                }
            }

            this.stats.totalItems = totalItems;
            this.stats.totalSize = totalSize;
        }
        catch
        {
            // Failed to update cache stats
        }
    }

    /**
     * Get cache statistics
     */
    public getStats(): CacheStats
    {
        this.updateStats();

        return { ...this.stats };
    }

    /**
     * Get cache hit ratio
     */
    public getHitRatio(): number
    {
        const total = this.stats.hitCount + this.stats.missCount;

        return total > 0 ? this.stats.hitCount / total : 0;
    }

    /**
     * Get all cache keys (without prefix)
     */
    public getKeys(): string[]
    {
        if (!this.isLocalStorageAvailable())
        {
            return [];
        }

        try
        {
            return Object.keys(localStorage)
                .filter(key => key.startsWith(this.config.keyPrefix))
                .map(key => key.substring(this.config.keyPrefix.length));
        }
        catch
        {
            return [];
        }
    }

    /**
     * Get cache item metadata
     */
    public getMetadata(key: string): Omit<CacheItem, "data"> | null
    {
        if (!this.isLocalStorageAvailable())
        {
            return null;
        }

        try
        {
            const cacheKey = this.getCacheKey(key);
            const stored = localStorage.getItem(cacheKey);

            if (!stored)
            {
                return null;
            }

            const cacheItem: CacheItem = JSON.parse(stored) as CacheItem;
            const { data, ...metadata } = cacheItem;

            return metadata;
        }
        catch
        {
            return null;
        }
    }

    /**
     * Async wrapper for cache operations (useful for heavy data processing)
     */
    public async getAsync<T>(key: string): Promise<T | null>
    {
        return new Promise((resolve) =>
        {
            setTimeout(() =>
            {
                resolve(this.get<T>(key));
            }, 0);
        });
    }

    /**
     * Async wrapper for cache operations (useful for heavy data processing)
     */
    public async setAsync(key: string, data: unknown, ttl?: number): Promise<boolean>
    {
        return new Promise((resolve) =>
        {
            setTimeout(() =>
            {
                resolve(this.set(key, data, ttl));
            }, 0);
        });
    }

    /**
     * Memoization helper - cache function results
     */
    public memoize<TArgs extends unknown[], TReturn>(
        fn: (...args: TArgs) => TReturn,
        keyGenerator: (...args: TArgs) => string,
        ttl?: number
    ): (...args: TArgs) => TReturn
    {
        return (...args: TArgs): TReturn =>
        {
            const key = keyGenerator(...args);
            const cached = this.get<TReturn>(key);

            if (cached !== null)
            {
                return cached;
            }

            const result = fn(...args);

            this.set(key, result, ttl);

            return result;
        };
    }

    /**
     * Cleanup and destroy the cache instance
     */
    public destroy(): void
    {
        this.stopAutoCleanup();
        this.clear();
    }
}

// Export a default instance for convenience
export const socialCache = new SocialCache();

// Export utility functions
export const cacheUtils = {
    /**
     * Create a cache key from multiple parts
     */
    createKey: (...parts: (string | number)[]): string =>
    {
        return parts.join(":");
    },

    /**
     * Format cache size in human-readable format
     */
    formatSize: (bytes: number): string =>
    {
        const units = ["B", "KB", "MB", "GB"];

        let size = bytes;
        let unitIndex = 0;

        const threshold = 1024;

        while (size >= threshold && unitIndex < units.length - 1)
        {
            size /= threshold;
            unitIndex++;
        }

        const decimalPlaces = 2;

        return `${size.toFixed(decimalPlaces)} ${units[unitIndex]}`;
    },

    /**
     * Format duration in human-readable format
     */
    formatDuration: (ms: number): string =>
    {
        const secondsInMs = 1000;
        const minutesInSeconds = 60;
        const hoursInMinutes = 60;
        const daysInHours = 24;

        const seconds = Math.floor(ms / secondsInMs);
        const minutes = Math.floor(seconds / minutesInSeconds);
        const hours = Math.floor(minutes / hoursInMinutes);
        const days = Math.floor(hours / daysInHours);

        if (days > 0) return `${days}d ${hours % daysInHours}h`;
        if (hours > 0) return `${hours}h ${minutes % minutesInSeconds}m`;
        if (minutes > 0) return `${minutes}m ${seconds % minutesInSeconds}s`;

        return `${seconds}s`;
    }
};
